import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IMAGES } from "@/lib/site";

/**
 * One-time splash loader shown on the very first page load.
 *
 * Notes on cross-browser rendering:
 * - No filter / invert / mix-blend-mode / opacity hacks are applied to the logo.
 *   The original colored PNG is rendered as-is.
 * - The logo is preloaded via an <Image> object before the splash is shown so the
 *   user never sees a "white flash" on desktop browsers where image fetch is
 *   slower than the animation duration. A `<link rel="preload">` in public/index.html
 *   primes the browser cache before React mounts.
 * - Always falls back to a CSS wordmark if the logo image fails to load.
 */
export default function SplashLoader() {
  const [show, setShow] = useState(() => {
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem("stenzia_splash_shown");
  });
  const [imgReady, setImgReady] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  const dismissedRef = useRef(false);

  // Pre-load the logo before fading in the splash content.
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => {
      if (!cancelled) setImgReady(true);
    };
    img.onerror = () => {
      if (!cancelled) {
        setImgFailed(true);
        setImgReady(true);
      }
    };
    img.src = IMAGES.logo;
    // Safety net: if the image is taking too long, reveal anyway after 700 ms
    // so users never see a fully blank white splash on slow connections.
    const safety = setTimeout(() => {
      if (!cancelled) setImgReady(true);
    }, 700);
    return () => {
      cancelled = true;
      clearTimeout(safety);
    };
  }, [show]);

  // Auto-dismiss 1.5s after the logo is on-screen.
  useEffect(() => {
    if (!show || !imgReady || dismissedRef.current) return;
    dismissedRef.current = true;
    const t = setTimeout(() => {
      sessionStorage.setItem("stenzia_splash_shown", "1");
      setShow(false);
    }, 1500);
    return () => clearTimeout(t);
  }, [show, imgReady]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          data-testid="splash-loader"
          className="fixed inset-0 z-[200] bg-white flex items-center justify-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: imgReady ? 1 : 0, y: imgReady ? 0 : 12, scale: imgReady ? 1 : 0.96 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative flex flex-col items-center"
          >
            {!imgFailed ? (
              <img
                src={IMAGES.logo}
                alt="Stenzia Displays"
                width="220"
                height="80"
                fetchPriority="high"
                decoding="async"
                loading="eager"
                draggable="false"
                onLoad={() => setImgReady(true)}
                onError={() => {
                  setImgFailed(true);
                  setImgReady(true);
                }}
                data-testid="splash-logo"
                className="h-20 md:h-28 w-auto select-none"
                style={{
                  filter: "none",
                  mixBlendMode: "normal",
                  WebkitFilter: "none",
                  opacity: 1,
                  backgroundColor: "transparent",
                }}
              />
            ) : (
              <div
                data-testid="splash-logo-fallback"
                className="font-display tracking-[0.18em] text-charcoal text-3xl md:text-5xl select-none"
              >
                STENZIA
                <div className="mt-1 text-[10px] tracking-[0.4em] text-stenzia-green font-medium text-center">
                  DISPLAYS
                </div>
              </div>
            )}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: imgReady ? 1 : 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="mt-3 h-[2px] w-32 md:w-44 bg-stenzia-green origin-left"
              aria-hidden="true"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
